import { adminRpc } from "../../services/admin-rpc-client";
// The BARREL, not ./plugins/monitor-plugin-service directly: plugins self-register on import,
// and the barrel is what pulls their files in. Importing the service module alone gets you the
// registry class with an EMPTY registry — forMonitor() would return undefined for everything
// and this router would silently never fire. The views that own these menus (services,
// schedules) do not otherwise import the plugins.
import { MonitorPluginService } from "../../pages/monitor/plugins";
import { showMonitorForm, type MonitorFormTarget } from "./monitor-form";

// openMonitorForm routes a monitor to the RIGHT dialog: the kind-specific form when the
// monitor has a plugin, the generic form otherwise.
//
// WHY THIS EXISTS. The row menus opened `showMonitorForm` unconditionally — the GENERIC
// dialog (checker, alert channel, enable/fail-on toggles). But a system-monitor's actual
// thresholds (disk/memory/swap/load/temps + the per-mount override box) live in a DIFFERENT
// dialog, `showSystemMonitorForm`, which was reachable only from the monitor detail page.
// So an operator looking at a monitor that just went red, clicking the obvious thing, got a
// dialog that could not change the threshold that tripped it — with no sign another dialog
// existed. (TASK-20260713-monitor-thresholds-unreachable-from-job-table)
//
// The dispatch machinery already existed and was unused here: every kind declares the plugin it
// draws (`checkpointPlugin`) + `openForm`, and MonitorPluginService indexes them (`forMonitor`). This
// spends that. A monitor with no plugin — or a job with no monitor yet — falls through to the
// generic form exactly as before, which is `forMonitor`'s documented contract.
//
// The plugin is resolved by QUERY rather than threaded through each view's SELECT: the two
// callers (services, schedules) key on different columns (`monitor_uid` vs `job.monitor_uid`)
// and neither carried `plugin_name`. One lookup here beats plumbing a column through
// every view that grows a Monitor action.
export async function openMonitorForm(target: MonitorFormTarget): Promise<void> {
  const plugin = target.monitorUid ? await pluginForMonitor(target.monitorUid) : undefined;

  if (plugin) {
    plugin.openForm({ monitorUid: target.monitorUid });
    return;
  }

  showMonitorForm(target);
}

// pluginForMonitor reads the monitor's plugin_name and resolves it to its kind.
// Returns undefined when the monitor is gone or names no plugin —
// every one of which means "use the generic form", so a failure here degrades to the old
// behaviour rather than breaking the menu.
async function pluginForMonitor(monitorUid: string) {
  try {
    const r = await adminRpc.query(
      `SELECT plugin_name, plugin_fileset_uid, extra_data FROM monitor WHERE uid = '${monitorUid.replace(/'/g, "''")}' LIMIT 1`,
    );
    const row = r.rows[0] as { plugin_name?: string | null; plugin_fileset_uid?: string | null; extra_data?: unknown } | undefined;
    if (!row) return undefined;
    return MonitorPluginService.get().forMonitor(row);
  } catch {
    return undefined;
  }
}
